import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Lock, CheckCircle, Clock } from 'lucide-react';
import PremiumEscrowFlow from '../components/PremiumEscrowFlow';
import { usePlatformConfig } from '../context/PlatformConfigContext';
import { getDefaultMinimumTransactionAmount, getPayoutScheduleMessage } from '../utils/payoutSchedule';

export default function EscrowPage() {
  const { config: platformConfig } = usePlatformConfig();
  const minimumTransactionAmount = getDefaultMinimumTransactionAmount(platformConfig);
  const payoutSchedule = getPayoutScheduleMessage(new Date(), platformConfig);
  return (
    <div className="tt-product min-h-screen bg-background">
      <nav className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-primary">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="flex items-center gap-3 mb-8">
          <ShieldCheck className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold text-foreground">Payment Protection</h1>
        </div>

        <div className="bg-card rounded-xl shadow-sm border border-border p-8 space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">How are payments protected?</h2>
            <p className="text-muted-foreground leading-relaxed">
              Your payment is securely held by our third-party payment provider until the transaction conditions are met.
              This protects buyers from fraud and sellers from non-payment — nobody gets hurt.
            </p>
          </section>

          <PremiumEscrowFlow />

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">How TrustTrade Protects Your Payment</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex gap-3 p-4 bg-primary/10 rounded-lg">
                <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">1</div>
                <div>
                  <h3 className="font-semibold text-foreground">Create Transaction</h3>
                  <p className="text-sm text-muted-foreground">Buyer or seller creates a transaction with item details and price</p>
                </div>
              </div>
              <div className="flex gap-3 p-4 bg-primary/10 rounded-lg">
                <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">2</div>
                <div>
                  <h3 className="font-semibold text-foreground">Buyer Pays</h3>
                  <p className="text-sm text-muted-foreground">Buyer pays via Card or Ozow — payment is protected by our third-party payment provider, not paid to the seller yet</p>
                </div>
              </div>
              <div className="flex gap-3 p-4 bg-primary/10 rounded-lg">
                <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">3</div>
                <div>
                  <h3 className="font-semibold text-foreground">Seller Delivers</h3>
                  <p className="text-sm text-muted-foreground">Seller ships or delivers the item knowing payment is secured</p>
                </div>
              </div>
              <div className="flex gap-3 p-4 bg-primary/10 rounded-lg">
                <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">4</div>
                <div>
                  <h3 className="font-semibold text-foreground">Funds Released</h3>
                  <p className="text-sm text-muted-foreground">{payoutSchedule.copy}</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">Key Benefits</h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Lock className="w-5 h-5 text-success mt-0.5" />
                <div>
                  <h3 className="font-semibold text-foreground">Secure Payments</h3>
                  <p className="text-sm text-muted-foreground">Funds held securely until delivery confirmed</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-success mt-0.5" />
                <div>
                  <h3 className="font-semibold text-foreground">Buyer & Seller Protection</h3>
                  <p className="text-sm text-muted-foreground">Both sides are covered until the deal is done</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-success mt-0.5" />
                <div>
                  <h3 className="font-semibold text-foreground">When payment is released</h3>
                  <p className="text-sm text-muted-foreground">On a standard in-person or digital delivery, payment is released when the buyer confirms receipt, or after the inspection window if nobody raises a dispute. Courier deliveries are not released on a timer unless that option is explicitly turned on. Milestone projects are never released on a timer: the client approves each stage, or support resolves a dispute.</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">Fees</h2>
            <p className="text-muted-foreground leading-relaxed">
              TrustTrade charges a <strong>2% platform fee</strong> (minimum R5 on a standard transaction).
              Minimum transaction amount is <strong>R{minimumTransactionAmount.toFixed(0)}</strong>.
              On a standard transaction the buyer or the seller can pay the platform fee. On a milestone
              project the client pays it, calculated once across the whole project. Payment-provider charges
              are separate and shown at secure checkout before anyone pays.
            </p>
          </section>
        </div>
      </main>

      <footer className="border-t border-border py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-muted-foreground text-sm">© 2026 TrustTrade South Africa. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
